import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useSelector } from "react-redux";
import { Bell, Check, CreditCard } from "lucide-react";
import { toast } from "react-toastify";
import GitchaLogo from "@/images/gitcha-logo.svg";
import { formatPrice, windowStateOf } from "@/components/GitchaProWindow";
import { useGitchaProOffer } from "@/hooks/useGitchaPro";
import useGitchaProCheckout from "@/hooks/useGitchaProCheckout";
import { packageOf, usePlans } from "@/hooks/usePlans";
import { useAgentPublicProfile } from "@/hooks/useAgentPublicProfile";
import useIsMobile from "@/hooks/useIsMobile";
import ProArt, { listingOf } from "./ProArt";
import KeepPro, { PlanCard } from "./KeepPro";
import AskBroker, { AskBrokerLine } from "./AskBroker";
import { canAskBroker, featureHeadline, featureWhy, glueLast, longDay, proStage, trialSteps } from "./proUpgradeModel";
import "./ProUpgrade.scss";

const STEP_ICONS = { today: Check, reminder: Bell, billed: CreditCard };

export const TrialSteps = ({ steps }) =>
  steps.length ? (
    <ol className="pro-steps">
      {steps.map((step, index) => {
        const Icon = STEP_ICONS[step.key] || Check;
        return (
          <li key={step.key} className="pro-steps__step" style={{ "--i": index }}>
            <span className={`pro-steps__node${index === 0 ? " is-now" : ""}`} aria-hidden="true">
              <Icon size={12} strokeWidth={2.5} />
            </span>
            <span className="pro-steps__when">{step.when}</span>
            <span className="pro-steps__what">{step.what}</span>
          </li>
        );
      })}
    </ol>
  ) : null;

const Skeleton = () => (
  <div className="pro-upgrade__skeleton" aria-busy="true" aria-label="Loading Gitcha Pro">
    <span />
    <span />
    <span />
  </div>
);

const ProUpgrade = ({
  show,
  onHide,
  feature,
  wantListingTitle,
  wantListing,
  userDetails,
  onCancelPlan,
  onChanged,
  onUnlocked,
  initialView = null,
}) => {
  const isMobile = useIsMobile();
  const storedDetails = useSelector((state) => state.userProfile?.userDetails);
  const details = userDetails?.FirstName ? userDetails : storedDetails?.[0] || userDetails;
  const offerQuery = useGitchaProOffer(Boolean(show));
  const plansQuery = usePlans(Boolean(show));
  const offer = offerQuery.data;
  const pkg = packageOf(plansQuery.data, "pro");
  const stage = offerQuery.isError ? "error" : proStage(windowStateOf(offer), pkg, plansQuery);
  const [view, setView] = useState(initialView);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    if (show) setView(initialView);
  }, [show, initialView]);
  const needsListing = Boolean(show) && !wantListing && !wantListingTitle && (feature === "video" || feature === "shareCard");
  const profile = useAgentPublicProfile(needsListing ? details?.slug : null).data;
  const listing = listingOf(wantListing || profile?.activeWantAds?.[0], wantListingTitle);
  const price = formatPrice(offer?.price?.amount);
  const brokerageName = plansQuery.data?.audience?.brokerage?.name;
  const showAsk = canAskBroker(plansQuery.data, stage);

  const close = () => {
    setView(initialView);
    onHide();
  };
  const finish = (message, unlocked) => {
    toast.dismiss();
    toast.success(message);
    onChanged && onChanged();
    setView(initialView);
    onHide();
    if (unlocked && onUnlocked) onUnlocked();
  };
  const checkout = useGitchaProCheckout({
    show: Boolean(show),
    onTrialStarted: (next) => finish(`Gitcha Pro is on until ${longDay(next?.trialEndsOn) || "your trial ends"}.`, true),
    onKept: (next) => finish(`Gitcha Pro continues on ${longDay(next?.trialEndsOn)}.`, false),
    onUpgraded: () => finish("You have Gitcha Pro.", true),
  });

  const keeping = view === "keep" || stage === "trial";
  const sheet = isMobile;
  const withArt = !keeping && (stage === "offer" || stage === "upgrade");

  const body = () => {
    if (stage === "loading") return <Skeleton />;
    if (stage === "error" || stage === "unavailable") {
      return (
        <>
          <h2 className="pro-upgrade__title">Gitcha Pro could not load right&nbsp;now</h2>
          <p className="pro-upgrade__why">
            {stage === "error"
              ? "Please try again in a moment."
              : "Gitcha Pro is available to agents who sign in through their MLS Buyer Listing Service."}
          </p>
          <button type="button" className="btn btn-primary pro-upgrade__cta" onClick={close}>
            Close
          </button>
        </>
      );
    }
    if (stage === "covered") {
      return (
        <>
          <h2 className="pro-upgrade__title">{pkg?.coveredBy || brokerageName || "Your brokerage"} covers your Gitcha&nbsp;Pro</h2>
          <p className="pro-upgrade__why">Your buyer listing form, videos and branded share cards are on. Nothing is billed to you.</p>
          <button type="button" className="btn btn-primary pro-upgrade__cta" onClick={close}>
            Done
          </button>
        </>
      );
    }
    if (stage === "pro") {
      const rows = [
        ["Plan", pkg?.planName || details?.PlanName],
        ["Price", pkg?.price?.monthly && pkg?.period !== "year" ? `${formatPrice(pkg.price.monthly)} a month` : pkg?.price?.yearly && pkg?.period === "year" ? `${formatPrice(pkg.price.yearly)} a year` : null],
        ["Renews", longDay(pkg?.renewsAt || details?.ToDate)],
      ].filter(([, value]) => Boolean(value));
      return (
        <div className="pro-review">
          <h2 className="pro-upgrade__title">You're a Gitcha&nbsp;Pro</h2>
          {offer?.keepingPro ? (
            <p className="pro-upgrade__why">Gitcha Pro continues on {longDay(offer?.trialEndsOn)}.</p>
          ) : null}
          <div className="pro-review__grid">
            <PlanCard />
            <div className="pro-review__side">
              <dl className="pro-review__summary">
                {rows.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              <div className="pro-review__actions is-reversed">
                <button type="button" className="btn btn-primary pro-upgrade__cta" onClick={close}>
                  Done
                </button>
                {onCancelPlan && !offer?.keepingPro && !offer?.brokeragePlus ? (
                  <button type="button" className="pro-review__cancel" onClick={onCancelPlan}>
                    Cancel plan
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (keeping) {
      return <KeepPro offer={offer} pkg={pkg} checkout={checkout} onBack={stage === "trial" ? null : () => setView(null)} onClose={close} />;
    }
    const steps = stage === "offer" ? trialSteps({ trialDays: offer?.trialDays, price }) : [];
    return (
      <>
        {withArt && !sheet ? <img src={GitchaLogo} alt="Gitcha" className="pro-upgrade__logo" /> : null}
        <h2 className="pro-upgrade__title">{glueLast(featureHeadline(feature, wantListingTitle || listing.title))}</h2>
        <p className="pro-upgrade__why">{featureWhy(feature)}</p>
        {stage === "offer" ? (
          <TrialSteps steps={steps} />
        ) : (
          <p className="pro-upgrade__price-line">{price} a month, starting today.</p>
        )}
        {checkout.error ? (
          <p className="pro-upgrade__error" role="alert">
            {checkout.error}
          </p>
        ) : null}
        <button
          type="button"
          className="btn btn-primary pro-upgrade__cta"
          disabled={checkout.busy}
          onClick={() => {
            checkout.setError(null);
            if (stage === "offer") checkout.startTrial.mutate();
            else setView("keep");
          }}
        >
          {checkout.startTrial.isPending ? "Starting your trial" : stage === "offer" ? "Try Gitcha Pro free" : `Upgrade for ${price} a month`}
        </button>
        <button type="button" className="pro-upgrade__later" onClick={close}>
          Not now
        </button>
        {showAsk ? <AskBrokerLine brokerageName={brokerageName} onAsk={() => setAsking(true)} enabled={Boolean(show)} /> : null}
      </>
    );
  };

  return (
    <>
      <Modal
        show={Boolean(show) && !checkout.covered && !asking}
        onHide={close}
        centered={!sheet}
        className={`pro-upgrade-modal${sheet ? " is-sheet" : ""}${(keeping || stage === "pro") && !sheet ? " is-review" : ""}`}
        aria-labelledby="pro-upgrade-title"
      >
        <div className="pro-upgrade">
          {withArt && !sheet ? (
            <div className="pro-upgrade__art">
              <button type="button" className="pro-upgrade__close is-light" aria-label="Close" onClick={close}>
                <span aria-hidden="true">&times;</span>
              </button>
              <span className="pro-upgrade__art-echo" aria-hidden="true" />
              <div className="pro-upgrade__art-card">
                <ProArt feature={feature} details={details} listing={listing} />
              </div>
            </div>
          ) : null}
          <div className="pro-upgrade__body" id="pro-upgrade-title">
            {sheet ? <span className="pro-upgrade__grab" aria-hidden="true" /> : null}
            {sheet || !withArt ? (
              <div className="pro-upgrade__head">
                <img src={GitchaLogo} alt="Gitcha" className="pro-upgrade__logo" />
                <button type="button" className="pro-upgrade__close" aria-label="Close" onClick={close}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            ) : null}
            {sheet && withArt ? <ProArt feature={feature} details={details} listing={listing} compact /> : null}
            {body()}
          </div>
        </div>
      </Modal>
      {checkout.modals}
      {brokerageName ? <AskBroker show={asking} onHide={() => setAsking(false)} brokerageName={brokerageName} /> : null}
    </>
  );
};

export default ProUpgrade;
